import React from "react";
import SEO from "../components/SEO";
import { PHONE, PHONE_TEL, BRAND } from "../data/content";

export default function Privacy() {
  return (
    <>
      <SEO
        title="Privacy Policy | Tech Emergency Adelaide"
        description="How Tech Emergency collects, stores and protects your personal information — written in plain English for our Adelaide customers."
        path="/privacy"
      />

      <section className="pt-20 pb-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-overline mb-4">/ Privacy</div>
          <h1 className="text-5xl sm:text-6xl font-bold tracking-tighter leading-none mb-6">
            Privacy Policy.
          </h1>
          <p className="text-zinc-400 text-lg leading-relaxed">
            Plain English. No dark patterns. Last updated {new Date().toLocaleDateString("en-AU", { year: "numeric", month: "long" })}.
          </p>
        </div>
      </section>

      <section className="pb-24" data-testid="privacy-content">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10 text-zinc-300 leading-relaxed">

          <div>
            <h2 className="text-2xl font-bold text-white mb-3">Who we are</h2>
            <p>
              {BRAND} is an Adelaide-based mobile IT and cyber-security business
              (ABN 96 584 230 274). When this policy says "we", "us" or "our", we mean Tech Emergency.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-3">What information we collect</h2>
            <p className="mb-3">When you book a job or contact us, we collect only what we need:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Your name</li>
              <li>Your phone number</li>
              <li>Your email address (optional)</li>
              <li>The suburb you want serviced</li>
              <li>The service you're after and a short description of the problem</li>
              <li>Preferred time, if you give us one</li>
            </ul>
            <p className="mt-3">
              We do <strong className="text-white">not</strong> collect your date of birth, government
              IDs, financial details or any sensitive information through the website.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-3">Why we collect it</h2>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>To contact you about your booking</li>
              <li>To dispatch a technician to your suburb</li>
              <li>To follow up on warranty work and customer care</li>
              <li>To respond to questions you've sent us</li>
            </ul>
            <p className="mt-3">
              That's it. We do not sell, rent or trade your information to anyone.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-3">How long we keep it</h2>
            <p>
              Booking and customer records are kept for as long as we have a working relationship with you,
              and for a reasonable period afterwards for warranty, accounting and Australian tax-record
              obligations. You can ask us to delete your record at any time and we will, unless we are
              legally required to keep it.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-3">Cookies & website analytics</h2>
            <p className="mb-3">
              This website does not run third-party advertising, behavioural tracking or fingerprinting.
              We use only essential cookies and local storage required for the site to function (for example,
              keeping an admin staff member logged in to the admin dashboard).
            </p>
            <p>
              We may use basic, anonymised traffic analytics (page views, popular suburbs, referrer)
              to understand which content is useful. We do not use this data to identify individuals.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-3">On-site data we access during a job</h2>
            <p>
              When we attend an on-site visit — repairing a computer, recovering data, configuring email,
              setting up a phone system — we may unavoidably see data on your devices. We treat everything
              we see as <strong className="text-white">strictly confidential</strong>. Your technician is
              police-checked. We never copy, transfer, share or photograph your personal files unless you
              have explicitly asked us to (e.g. data recovery, data migration).
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-3">Security</h2>
            <p>
              Booking information is stored in a private, password-protected database. Admin access is
              behind multi-factor protections. We never store credit card numbers or passwords for your
              other services.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-3">Your rights</h2>
            <p className="mb-3">Under Australian Privacy Principles you can ask us, at any time, to:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Tell you what personal information we hold about you</li>
              <li>Correct anything that's wrong</li>
              <li>Delete your record (subject to legal/tax retention)</li>
              <li>Stop contacting you</li>
            </ul>
            <p className="mt-3">
              Just call us on <a className="text-white underline-offset-4 hover:underline" href={PHONE_TEL}>{PHONE}</a> or email us — we'll action it.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-white mb-3">Complaints</h2>
            <p>
              If you're unhappy with how we've handled your data, contact us first — we'll work it out.
              If we can't resolve it, you can lodge a complaint with the Office of the Australian Information
              Commissioner (OAIC) at <a className="text-white underline-offset-4 hover:underline" href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer">oaic.gov.au</a>.
            </p>
          </div>

          <div className="te-card p-6">
            <div className="te-overline mb-2">/ Questions?</div>
            <p className="text-sm text-zinc-300">
              Call us on <a className="text-white font-medium" href={PHONE_TEL}>{PHONE}</a> — Mon–Sat, 9am–7pm.
              Real Adelaide person, real answer.
            </p>
          </div>

        </div>
      </section>
    </>
  );
}
